/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { BBSB_SPORTS } from '@constants/strings';
import { useHistory, useLocation } from 'react-router-dom';

interface UseTabs {
  tabs: string[];
  tabIndex: number;
  setTabIndex: (index: number) => void;
  onActive: (index: number) => void;
}

const DEFAULT_TABS = ['Schedule', 'Highlights', 'HitScope', 'Roster'];
const NON_BBSB_TABS = ['Schedule', 'Roster'];

const useTabs = (
  teamId: string,
  isAdmin: boolean,
  sportType: string,
  isScoringLink: boolean
): UseTabs => {
  const location = useLocation();
  const history = useHistory();

  const [tabIndex, setTabIndex] = useState<number>(0);

  const tabs = useMemo(() => {
    if (isScoringLink) {
      setTabIndex(0);
      return ['Schedule'];
    }

    if (!BBSB_SPORTS.includes(sportType)) {
      return NON_BBSB_TABS;
    }
    return DEFAULT_TABS;
  }, [isScoringLink, sportType]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    if (isScoringLink) {
      setTabIndex(0);
    }
    if (tabParam) {
      const newIndex = tabs.findIndex(
        (tab) => tab.toLowerCase() === tabParam.toLowerCase()
      );
      if (newIndex !== -1 && newIndex !== tabIndex) {
        setTabIndex(newIndex);
      }
    }
  }, [location.search, teamId, tabs]);

  useEffect(() => {
    if (tabIndex >= tabs.length) {
      setTabIndex(Math.max(0, tabs.length - 1));
    }
  }, [tabs]);

  const onActive = useCallback(
    (index: number) => {
      if (isScoringLink || index < 0 || index >= tabs.length) {
        return;
      }

      setTabIndex(index);
      const tabName = tabs[index].toLowerCase();

      if (isAdmin) {
        history.replace(`${location.pathname}?tab=${tabName}`);
      }
    },
    [history, location.pathname, isAdmin, tabs, sportType]
  );

  return { tabs, tabIndex, setTabIndex, onActive };
};

export default useTabs;
